import React from 'react';
import { ObservableQueryFields, QueryDataOptions, useQuery } from '@apollo/client/react';
import { OperationVariables } from '@apollo/client';
import { Box } from '@material-ui/core';
import { Redirect } from 'react-router-dom';
import { useAuth } from '../utils/hooks/auth';
import { BaseGqlComponentProps, LoaderElement } from './common';

interface ChildrenProps<TData, TVariables> extends ObservableQueryFields<TData, TVariables> {
    data: TData;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface QueryProps<TData = any, TVariables = OperationVariables>
    extends Omit<QueryDataOptions<TData, TVariables>, 'children'>, BaseGqlComponentProps<TVariables>
{
    children: (result: ChildrenProps<TData, TVariables>) => JSX.Element;
}

const Query = <TData, TVariables = OperationVariables>({
    client,
    query,
    children,
    variables,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Loader = LoaderElement,
    redirectOnErrorUrl,
    ...otherProps
}: QueryProps<TData, TVariables>) => {
    const { loading, error, data, ...other } = useQuery<TData, TVariables>(query, { variables, ...otherProps });

    const { logout } = useAuth();
    if (loading) {
        return (
            <Box display="flex" justifyContent='center' alignItems='center' flexGrow={1}>
                <Loader />
            </Box>
        );
    }

    if (error) {
        const authError = error.graphQLErrors[0]?.extensions?.exception?.status === 401;
        if (authError) {
            setTimeout(() => {
                logout();
            }, 500);
        }

        if (redirectOnErrorUrl) {
            return <Redirect to={redirectOnErrorUrl} />;
        }

        return (
            <div>
                {error.message}
            </div>
        );
    }

    if (data) {
        return children({ data, ...other });
    }

    // Can this actually happen? It shouldn't be null
    return null;
};

export default Query;
